import { isValid, parseISO } from 'date-fns';
import { formatTimestamp } from 'shared/dates';
import { UrlRegex } from '../../../../../constants';

const HTTPS_PREFIX = 'https://';

// Heuristic for dynamic, untyped attributes: format as a date only when the key
// looks time-related AND the value lands in a sane window.
const MIN_PLAUSIBLE_MS = Date.UTC(2000, 0, 1);
const FUTURE_TOLERANCE_MS = 1000 * 60 * 60 * 24 * 365; // 1 year

// Strong terms that unambiguously signal a date/time value. Safe to match as
// substrings, so keys with a date term fused onto another word are still
// detected even when they can't be tokenized into words - e.g. "LOADDATE" or
// the "filedate"/"ingestdate" tokens in "idp_filedate"/"idp_ingestdate".
const STRONG_TIMESTAMP_KEY_TERMS = ['datetime', 'timestamp', 'date', 'time'];

// Whole-word key tokens signalling a date/time value (e.g. "acq_date" -> date).
const TIMESTAMP_KEY_TERMS = new Set([
  'date',
  'datetime',
  'time',
  'timestamp',
  'epoch',
  'created',
  'create',
  'updated',
  'update',
  'modified',
  'modify',
  'published',
  'publish',
  'issued',
  'observed',
  'reported',
  'effective',
  'start',
  'end',
  'begin',
  'expires',
  'expired',
  'expiry',
  'seen',
  'at',
  'on',
  'ts',
]);

export const isHttpsUrl = (value: string): boolean =>
  value.startsWith(HTTPS_PREFIX) && UrlRegex.test(value);

const IMAGE_EXTENSION_RE = /\.(jpe?g|png|gif|webp|bmp|svg|avif)$/i;

// Detects an image by pathname extension, ignoring any query string
// or fragment. Assumes a valid URL (gated behind isHttpsUrl).
export const isImageExtensionUrl = (value: string): boolean => {
  try {
    const { pathname } = new URL(value);
    return IMAGE_EXTENSION_RE.test(pathname);
  } catch {
    return false;
  }
};

const tokenizeKey = (key: string): string[] =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // separate camelCase words
    .toLowerCase()
    .split(/[^a-z0-9]+/) // split on spaces, underscores, etc.
    .filter(Boolean);

export const isTimestampKey = (key: string): boolean => {
  const normalizedKey = key.toLowerCase();
  if (STRONG_TIMESTAMP_KEY_TERMS.some((term) => normalizedKey.includes(term))) {
    return true;
  }
  return tokenizeKey(key).some((token) => TIMESTAMP_KEY_TERMS.has(token));
};

export const isPlausibleTimestampMs = (ms: number): boolean => {
  if (!isValid(new Date(ms))) return false;
  return ms >= MIN_PLAUSIBLE_MS && ms <= Date.now() + FUTURE_TOLERANCE_MS;
};

export const epochToMillis = (value: unknown): number | null => {
  const num = Number(value);
  if (!Number.isInteger(num) || num <= 0) return null;

  if (isPlausibleTimestampMs(num)) return num; // already milliseconds

  const asMillis = num * 1000;
  if (isPlausibleTimestampMs(asMillis)) return asMillis; // epoch seconds

  return null;
};

// Accept only a complete ISO 8601 timestamp that unambiguously identifies an
// instant: full date, full time (to seconds), and an explicit timezone (Z or
// ±HH:MM). Date-only ("2021-10-29") and timezone-naive ("2021-10-29T08:00:00")
// forms are intentionally rejected - without a timezone they'd render on a
// different calendar day depending on the user's location.
const ISO_DATETIME_RE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

export const isoStringToMillis = (value: unknown): number | null => {
  if (typeof value !== 'string' || !ISO_DATETIME_RE.test(value)) return null;

  // parseISO validates the calendar (e.g. rejects Feb 30), which Date.parse
  // does not - it would silently roll the date forward to Mar 2.
  const ms = parseISO(value).getTime();
  if (!isPlausibleTimestampMs(ms)) return null;

  return ms;
};

export const tryFormatTimestamp = (
  key: string,
  value: unknown,
): string | null => {
  if (!isTimestampKey(key)) return null;

  const ms = epochToMillis(value) ?? isoStringToMillis(value);
  if (ms === null) return null;

  return formatTimestamp(ms);
};
